import { useEffect, useState, useCallback } from "react";
import { Plus, Flame, Clock, Loader2, Inbox } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, ALL_TAB } from "@/lib/constants";
import { IssueCard } from "@/components/IssueCard";
import { CreateIssueModal } from "@/components/CreateIssueModal";
import { AuthModal } from "@/components/AuthModal";
import { CoinBadge } from "@/components/CoinBadge";
import { Button } from "@/components/ui/button";

const TABS = [ALL_TAB, ...CATEGORIES];

export default function Feed() {
  const { user } = useAuth();
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("recent");
  const [issues, setIssues] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  const load = useCallback(async () => {
    setIssues(null);
    const { data } = await api.get("/issues", { params: { category, sort } });
    setIssues(data);
  }, [category, sort]);

  useEffect(() => { load(); }, [load]);

  const onCreated = (issue) => setIssues((prev) => [issue, ...(prev || [])]);
  const onUpdate = (updated) =>
    setIssues((prev) => (prev || []).map((i) => (i.id === updated.id ? updated : i)));

  const startPost = () => (user ? setCreateOpen(true) : setAuthOpen(true));

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">Citizen Issue Feed</h1>
          <p className="text-sm text-muted-foreground">Raise it. Back it. Solve it — together.</p>
        </div>
        {user && <CoinBadge value={user.coins} />}
      </div>

      {/* Create widget */}
      <button
        data-testid="create-issue-button"
        onClick={startPost}
        className="w-full mb-5 flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3.5 text-left hover:border-saffron/50 transition-colors"
      >
        <span className="grid place-items-center h-9 w-9 rounded-lg bg-saffron text-primary-foreground">
          <Plus size={18} />
        </span>
        <span className="text-muted-foreground text-sm">
          {user ? "Raise a local issue and earn 3 Dimagi coins…" : "Join to raise your first local issue…"}
        </span>
      </button>

      {/* Category tabs */}
      <div data-testid="feed-category-tabs" className="flex gap-2 overflow-x-auto scrollbar-thin pb-2 -mx-1 px-1">
        {TABS.map((t) => {
          const active = category === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`feed-tab-${t.key}`}
              onClick={() => setCategory(t.key)}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-all ${
                active ? "border-transparent text-white" : "border-border text-muted-foreground hover:text-foreground"
              }`}
              style={active ? { background: t.color, color: t.key === "all" ? "#0A0B0E" : "#fff" } : {}}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Sort */}
      <div className="flex items-center gap-2 mt-3 mb-4">
        <Button size="sm" variant={sort === "recent" ? "secondary" : "ghost"} onClick={() => setSort("recent")} className="gap-1.5" data-testid="sort-recent">
          <Clock size={14} /> Recent
        </Button>
        <Button size="sm" variant={sort === "top" ? "secondary" : "ghost"} onClick={() => setSort("top")} className="gap-1.5" data-testid="sort-top">
          <Flame size={14} /> Most backed
        </Button>
      </div>

      {/* List */}
      {issues === null ? (
        <div className="grid place-items-center py-20 text-muted-foreground">
          <Loader2 className="animate-spin" size={28} />
        </div>
      ) : issues.length === 0 ? (
        <div className="grid place-items-center py-20 text-center text-muted-foreground">
          <Inbox size={34} className="mb-3" />
          <p className="font-medium">No issues here yet.</p>
          <p className="text-sm">Be the first to raise one in this category.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {issues.map((issue) => (
            <IssueCard key={issue.id} issue={issue} onUpdate={onUpdate} onRequireAuth={() => setAuthOpen(true)} />
          ))}
        </div>
      )}

      <CreateIssueModal open={createOpen} onOpenChange={setCreateOpen} onCreated={onCreated} />
      <AuthModal open={authOpen} tab="register" onOpenChange={setAuthOpen} />
    </div>
  );
}
